import { useUser } from '@portals-platform/auth';
import type { NavSection } from '@portals-platform/ui';
import { AdminShell, AdminSidebar, AdminTopbar } from '@portals-platform/ui';
import { FileBarChartIcon, FileTextIcon, LayoutDashboardIcon, RefreshCwIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { brandInitials, useBrandConfig } from '../brand-config-context.js';
import { useCanViewReports } from '../data-access/index.js';
import { AppLink } from './app-link.js';

function buildNavSections(activeId: string, canViewReports: boolean): NavSection[] {
  return [
    {
      label: 'Workspace',
      items: [
        {
          id: 'overview',
          label: 'Overview',
          icon: LayoutDashboardIcon,
          href: '/',
          active: activeId === 'overview',
        },
        {
          id: 'certificates',
          label: 'Certificates',
          icon: FileTextIcon,
          href: '/certificates',
          active: activeId === 'certificates',
        },
        {
          id: 'renewals',
          label: 'Renewals',
          icon: RefreshCwIcon,
          href: '/renewals',
          active: activeId === 'renewals',
        },
      ],
    },
    {
      label: 'Insights',
      items: [
        {
          id: 'reports',
          label: 'Reports',
          icon: FileBarChartIcon,
          href: '/reports',
          active: activeId === 'reports',
        },
      ].filter((item) => item.id !== 'reports' || canViewReports),
    },
  ].filter((section) => section.items.length > 0);
}

interface PortalShellProps {
  activeId: string;
  pageTitle: string;
  children: ReactNode;
}

export function PortalShell({ activeId, pageTitle, children }: PortalShellProps) {
  const brand = useBrandConfig();
  const mark = brandInitials(brand.name);
  const userName = useUser()?.name ?? '';
  const { canViewReports } = useCanViewReports();
  const [isDark, setIsDark] = useState(
    () =>
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      (!document.documentElement.hasAttribute('data-theme') &&
        window.matchMedia('(prefers-color-scheme: dark)').matches),
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const logoUrl = isDark ? brand?.assets?.logoInverseUrl : brand?.assets?.logoUrl;

  const breadcrumbs = [
    { label: brand.name, href: '/' },
    { label: 'Certificate Portal', href: '/' },
    { label: pageTitle },
  ];

  return (
    <AdminShell
      sidebar={
        <AdminSidebar
          brandName={brand.name}
          brandSub="Certificate Portal"
          brandMark={mark}
          brandLogoUrl={logoUrl ?? ''}
          navSections={buildNavSections(activeId, canViewReports)}
          workspaceName={brand.name}
          workspacePlan="Enterprise · 140 seats"
          workspaceInitials={mark}
          linkComponent={AppLink}
        />
      }
      topbar={
        <AdminTopbar
          breadcrumbs={breadcrumbs}
          searchPlaceholder="Search certificates, programs, renewals…"
          periodLabel="Q1 2026"
          userInitials={brandInitials(userName)}
          userName={userName}
          userRole=""
          notificationCount={3}
          isDark={isDark}
          onToggleTheme={() => setIsDark((d) => !d)}
          linkComponent={AppLink}
        />
      }
    >
      {children}
    </AdminShell>
  );
}
